/**
 * i18n voor Chakra Architect UI-chrome.
 *
 * Bron = Nederlands; Engels is fallback bij andere systeemtalen. Content
 * (kaartnamen/teksten/monologen) staat in de data in het NL; de Engelse
 * content-overlay wordt later build-time gegenereerd (fase 9). Dit dekt alleen
 * de vaste UI-labels.
 */
import { detecteerTaal as detecteerBasisTaal } from '../i18n';

export type Taal = 'nl' | 'en';

type Dict = Record<string, string>;

const nl: Dict = {
  'ui.energie': 'Energie',
  'ui.beurt': 'Beurt {n}',
  'ui.eindigBeurt': 'Beurt beëindigen',
  'ui.schild': 'Schild',
  'ui.verbinding': 'Verbinding',
  'ui.hand': 'Hand',
  'ui.deck': 'Trekstapel',
  'ui.afleg': 'Aflegstapel',
  'ui.intent': 'Volgende aanval',
  'ui.geenIntent': 'De baas verzamelt kracht…',
  'ui.aura': 'Actieve praktijk',
  'ui.gewonnen': 'Overwonnen',
  'ui.verloren': 'Het wiel valt stil',
  'ui.opnieuw': 'Opnieuw',
  'ui.bevroren': 'Bevroren',
  'ui.kiesBaas': 'Kies een tegenstander (chakra 1 — demo)',
  'ui.start': 'Begin gevecht',
  'ui.log': 'Verloop',
  'ui.subbaas': 'Sub-baas',
  'ui.eindbaas': 'Eindbaas',
  'ui.reisdagboek': 'Reisdagboek',
  'ui.archief': 'Archief',
  'ui.codex': 'Codex',
  'ui.terug': 'Terug',
  'ui.geenSave': 'Voortgang wordt deze sessie niet bewaard.',
  'ui.meesterschap': 'Meesterschapskaart ontgrendeld',
  'ui.chakraVoltooid': 'Chakra in balans',
  'ui.reisLeeg': 'Je reis is nog niet begonnen.',
  'ui.resource': 'Resource',
  'ui.wereld': 'Wereld',
  'ui.instellingen': 'Instellingen',
  'ui.volume': 'Volume',
  'ui.dempen': 'Dempen',
  'ui.reset': 'Voortgang wissen',
  'ui.resetBevestig': 'Weet je het zeker? Alle voortgang gaat verloren.',
  'ui.ja': 'Ja, wissen',
  'ui.annuleer': 'Annuleren',
  'vn.verder': 'Verder',
  'vn.beginGevecht': 'Begin gevecht',
  'vn.kiesHouding': 'Hoe sta je tegenover deze schaduw?',
  'vn.reflectie': 'Een moment van reflectie',
  'houding.erkennen': 'Erkennen',
  'houding.bevragen': 'Bevragen',
  'houding.confronteren': 'Confronteren',
  'houding.erkennen.bonus': '+3 schild bij start',
  'houding.bevragen.bonus': '+2 kaarten in eerste hand',
  'houding.confronteren.bonus': 'eerste aanval −2 schade',
  'type.s': 'Steen',
  'type.o': 'Orgaan',
  'type.p': 'Praktijk',
  'type.e': 'Element',
  'type.m': 'Mantra',
  'type.k': 'Kleur',
};

const en: Dict = {
  'ui.energie': 'Energy',
  'ui.beurt': 'Turn {n}',
  'ui.eindigBeurt': 'End turn',
  'ui.schild': 'Shield',
  'ui.verbinding': 'Connection',
  'ui.hand': 'Hand',
  'ui.deck': 'Deck',
  'ui.afleg': 'Discard',
  'ui.intent': 'Next attack',
  'ui.geenIntent': 'The boss gathers strength…',
  'ui.aura': 'Active practice',
  'ui.gewonnen': 'Overcome',
  'ui.verloren': 'The wheel falls still',
  'ui.opnieuw': 'Again',
  'ui.bevroren': 'Frozen',
  'ui.kiesBaas': 'Choose an opponent (chakra 1 — demo)',
  'ui.start': 'Begin battle',
  'ui.log': 'Log',
  'ui.subbaas': 'Sub-boss',
  'ui.eindbaas': 'Final boss',
  'ui.reisdagboek': 'Journal',
  'ui.archief': 'Archive',
  'ui.codex': 'Codex',
  'ui.terug': 'Back',
  'ui.geenSave': 'Progress will not be saved this session.',
  'ui.meesterschap': 'Mastery card unlocked',
  'ui.chakraVoltooid': 'Chakra in balance',
  'ui.reisLeeg': 'Your journey has not yet begun.',
  'ui.resource': 'Resource',
  'ui.wereld': 'World',
  'ui.instellingen': 'Settings',
  'ui.volume': 'Volume',
  'ui.dempen': 'Mute',
  'ui.reset': 'Clear progress',
  'ui.resetBevestig': 'Are you sure? All progress will be lost.',
  'ui.ja': 'Yes, clear',
  'ui.annuleer': 'Cancel',
  'vn.verder': 'Continue',
  'vn.beginGevecht': 'Begin battle',
  'vn.kiesHouding': 'How do you face this shadow?',
  'vn.reflectie': 'A moment of reflection',
  'houding.erkennen': 'Acknowledge',
  'houding.bevragen': 'Question',
  'houding.confronteren': 'Confront',
  'houding.erkennen.bonus': '+3 shield at start',
  'houding.bevragen.bonus': '+2 cards in first hand',
  'houding.confronteren.bonus': 'first attack −2 damage',
  'type.s': 'Stone',
  'type.o': 'Organ',
  'type.p': 'Practice',
  'type.e': 'Element',
  'type.m': 'Mantra',
  'type.k': 'Color',
};

const DICTS: Record<Taal, Dict> = { nl, en };

export function detecteerTaal(): Taal {
  const basis = detecteerBasisTaal();
  return basis === 'nl' ? 'nl' : 'en';
}

export function t(sleutel: string, taal: Taal, vars?: Record<string, string | number>): string {
  let tekst = DICTS[taal][sleutel] ?? DICTS.en[sleutel] ?? sleutel;
  if (vars) for (const [k, v] of Object.entries(vars)) tekst = tekst.replace(`{${k}}`, String(v));
  return tekst;
}
